"use client";

import React from "react";
import { useApp } from "./AppProvider";
import type { ClientItem } from "@/lib/cms";

interface ClientsCarouselProps {
  items: ClientItem[];
}

export default function ClientsCarousel({ items }: ClientsCarouselProps) {
  const { isRTL } = useApp();

  // If no items, don't render or show a subtle message
  if (!items || items.length === 0) return null;

  // Duplicate items for seamless loop
  const displayItems = [...items, ...items, ...items, ...items];

  const h = (en: string, ar: string) => isRTL ? (ar || en) : en;

  return (
    <section id="clients" style={{ padding: "6rem 0 4rem", overflow: "hidden", background: "var(--bg-primary)" }}>
      <div className="container" style={{ marginBottom: "3rem", textAlign: "center" }}>
        <div style={{ 
          display: "inline-flex", 
          alignItems: "center", 
          gap: "0.5rem", 
          padding: "0.4rem 1rem", 
          borderRadius: "50px", 
          background: "var(--bg-surface)", 
          border: "1px solid var(--border-subtle)", 
          color: "var(--color-purple)", 
          fontSize: "0.75rem", 
          fontWeight: 700, 
          letterSpacing: "0.05em", 
          textTransform: "uppercase",
          marginBottom: "1rem"
        }}>
          ✦ {h("Trusted Partners", "شركاؤنا الموثوقون")}
        </div>
        <h2 style={{ fontSize: "clamp(1.5rem, 3vw, 2.2rem)", fontWeight: 800, color: "var(--text-primary)", letterSpacing: "-0.01em" }}>
          {h("Powering leading organizations", "نمكن المؤسسات الرائدة بنجاح")}
        </h2>
      </div>

      <div className="container">
        <div className="glass-card" style={{ 
          position: "relative", 
          width: "100%", 
          padding: "3rem 0",
          borderRadius: "24px",
          overflow: "hidden"
        }}>
          <div className="marquee-wrapper" style={{ position: "relative", width: "100%", overflow: "hidden" }}>
            <div className="marquee-content" style={{
              display: "flex",
              gap: "5rem",
              width: "max-content",
              animation: `marquee ${items.length * 6}s linear infinite`,
              animationDirection: isRTL ? "reverse" : "normal"
            }}>
              {displayItems.map((client, idx) => (
                <div key={`${client.id}-${idx}`} className="client-logo-wrapper">
                  {client.logo?.url ? (
                    <img 
                      src={client.logo.url} 
                      alt={client.name} 
                      title={client.name}
                      className="client-logo"
                    />
                  ) : (
                    <span className="client-text">
                      {client.name}
                    </span>
                  )}
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      <style jsx>{`
        @keyframes marquee {
          0% { transform: translateX(0); }
          100% { transform: translateX(-50%); }
        }
        .marquee-wrapper::before,
        .marquee-wrapper::after {
          content: "";
          position: absolute;
          top: 0;
          width: 250px;
          height: 100%;
          z-index: 5;
          pointer-events: none;
        }
        .marquee-wrapper::before {
          left: 0;
          background: linear-gradient(to right, var(--bg-card) 10%, transparent);
        }
        .marquee-wrapper::after {
          right: 0;
          background: linear-gradient(to left, var(--bg-card) 10%, transparent);
        }
        
        @media (max-width: 768px) {
          .marquee-wrapper::before,
          .marquee-wrapper::after {
            width: 80px;
          }
          .marquee-content {
            gap: 3rem;
          }
        }

        .client-logo-wrapper {
          flex-shrink: 0;
          display: flex;
          align-items: center;
          justify-content: center;
          height: 60px;
          width: 180px;
          padding: 0 1.5rem;
          cursor: pointer;
        }
        
        .client-logo {
          max-height: 100%;
          max-width: 100%;
          object-fit: contain;
          filter: var(--logo-filter, grayscale(1) opacity(0.5));
          transition: all 0.5s cubic-bezier(0.4, 0, 0.2, 1);
        }

        .client-logo-wrapper:hover .client-logo {
          filter: var(--logo-filter-hover, grayscale(0) opacity(1));
          transform: scale(1.15);
        }

        .client-text {
          font-weight: 700;
          color: var(--text-secondary);
          font-size: 1.2rem;
          letter-spacing: 0.05em;
          white-space: nowrap;
          transition: all 0.5s cubic-bezier(0.4, 0, 0.2, 1);
        }

        .client-logo-wrapper:hover .client-text {
          color: var(--text-primary);
          transform: scale(1.15);
        }
      `}</style>
    </section>
  );
}
